import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Star, MapPin, RefreshCw, ExternalLink, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { GoogleBusinessRecord, GooglePlaceCandidate } from '../../lib/types';
import { Input, Checkbox } from '../../components/ui/Field';

// Estrellas (rellenas/medias/vacías) para un valor 0–5.
function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} de 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, value - i));
        return (
          <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
            <Star size={size} className="absolute inset-0 text-ui-text-subtle" />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Star size={size} className="text-amber-400 fill-amber-400" />
            </span>
          </span>
        );
      })}
    </span>
  );
}

function formatWhen(ms: number | null): string {
  if (!ms) return '';
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  return `hace ${Math.round(hrs / 24)} días`;
}

export default function GoogleBusinessTab() {
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState<GoogleBusinessRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Búsqueda / vinculación
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GooglePlaceCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.google.get()
      .then(setLink)
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar la conexión con Google.'))
      .finally(() => setLoading(false));
  }, []);

  async function runSearch() {
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const { results } = await api.google.search(query.trim());
      setResults(results);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo buscar tu negocio en Google.');
    } finally {
      setSearching(false);
    }
  }

  async function linkPlace(placeId: string) {
    setLinkingId(placeId);
    setError(null);
    try {
      const saved = await api.google.link(placeId);
      setLink(saved);
      setResults([]);
      setQuery('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo vincular tu negocio.');
    } finally {
      setLinkingId(null);
    }
  }

  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      setLink(await api.google.refresh());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron actualizar los datos.');
    } finally {
      setRefreshing(false);
    }
  }

  async function toggleVisibility(show: boolean) {
    if (!link) return;
    const prev = link;
    setLink({ ...link, showOnSite: show });
    try {
      setLink(await api.google.setVisibility(show));
    } catch {
      setLink(prev);
      setError('No se pudo cambiar la visibilidad en el sitio.');
    }
  }

  async function unlink() {
    if (!confirm('¿Quitar la conexión con Google? Podrás volver a vincularla cuando quieras.')) return;
    setError(null);
    try {
      await api.google.unlink();
      setLink(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar la conexión.');
    }
  }

  if (loading) return <p className="text-sm text-ui-text-muted">Cargando…</p>;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {!link ? (
        // ─── Sin vincular: buscar y elegir la ficha ───
        <div className="space-y-4">
          <p className="text-sm text-ui-text-muted">
            Busca tu negocio tal y como aparece en Google Maps para mostrar tu calificación y tus
            reseñas en el sitio, y para invitar a tus clientes a dejarte una reseña.
          </p>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Nombre y ciudad de tu negocio"
              className="flex-1 min-w-0"
            />
            <button
              onClick={runSearch}
              disabled={searching || !query.trim()}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90"
            >
              <Search size={16} />
              {searching ? 'Buscando…' : 'Buscar'}
            </button>
          </div>

          <AnimatePresence>
            {results.map((r) => (
              <motion.div
                key={r.placeId}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-between gap-3 bg-ui-bg border border-ui-border rounded-lg px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm text-ui-text truncate">{r.displayName}</p>
                  <p className="text-sm text-ui-text-subtle truncate flex items-center gap-1">
                    <MapPin size={13} /> {r.formattedAddress}
                  </p>
                </div>
                <button
                  onClick={() => linkPlace(r.placeId)}
                  disabled={linkingId !== null}
                  className="shrink-0 px-3 py-1.5 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90"
                >
                  {linkingId === r.placeId ? 'Conectando…' : 'Es mi negocio'}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        // ─── Vinculado: tarjeta de reputación ───
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
          <div className="bg-ui-bg border border-ui-border rounded-lg px-4 py-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-ui-text font-medium truncate">{link.displayName ?? 'Tu negocio'}</p>
                {link.formattedAddress && (
                  <p className="text-sm text-ui-text-subtle truncate flex items-center gap-1">
                    <MapPin size={13} /> {link.formattedAddress}
                  </p>
                )}
              </div>
              {link.googleMapsUri && (
                <a
                  href={link.googleMapsUri}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-1 text-sm text-ui-text-muted hover:text-ui-text"
                >
                  Ver en Maps <ExternalLink size={13} />
                </a>
              )}
            </div>

            <div className="flex items-center gap-3 pt-1">
              {link.rating != null ? (
                <>
                  <span className="text-2xl font-semibold text-ui-text">{link.rating.toFixed(1)}</span>
                  <Stars value={link.rating} size={18} />
                  <span className="text-sm text-ui-text-subtle">
                    {link.userRatingCount ?? 0} reseñas en Google
                  </span>
                </>
              ) : (
                <span className="text-sm text-ui-text-subtle">Todavía sin calificación en Google.</span>
              )}
            </div>

            <p className="text-sm text-ui-text-subtle">
              Datos de Google · actualizado {formatWhen(link.cachedAt)}
            </p>
          </div>

          {/* Reseñas recientes (hasta 5 que entrega Google) */}
          {link.reviews.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm text-ui-text-muted">Reseñas recientes</p>
              {link.reviews.map((rev, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-ui-bg border border-ui-border rounded-lg px-4 py-3 space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-ui-text">{rev.author || 'Anónimo'}</span>
                    <Stars value={rev.rating} size={13} />
                  </div>
                  {rev.text && <p className="text-sm text-ui-text-subtle">{rev.text}</p>}
                  {rev.relativeTime && <p className="text-sm text-ui-text-subtle">{rev.relativeTime}</p>}
                </motion.div>
              ))}
            </div>
          )}

          {/* Mostrar en el sitio + deep link de reseña */}
          <div className="bg-ui-bg border border-ui-border rounded-lg px-4 py-4 space-y-3">
            <Checkbox
              checked={link.showOnSite}
              onChange={(e) => toggleVisibility(e.target.checked)}
              label="Mostrar mi calificación y reseñas de Google en el sitio"
            />
            <p className="text-sm text-ui-text-subtle pl-6">
              Aparecerá como prueba social y ayuda a que tu negocio destaque en buscadores. Debes
              publicar el sitio para que el cambio sea visible.
            </p>
            {link.writeReviewUri && (
              <a
                href={link.writeReviewUri}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-ui-accent hover:underline pl-6"
              >
                Abrir el enlace para dejar una reseña <ExternalLink size={13} />
              </a>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ui-surface border border-ui-border text-ui-text text-sm rounded-md disabled:opacity-50 hover:bg-ui-surface-2"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Actualizando…' : 'Actualizar desde Google'}
            </button>
            <button
              onClick={unlink}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-ui-text-muted hover:text-red-300"
            >
              <Trash2 size={15} /> Quitar conexión
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
